import { Stack, styled } from "@mui/material";
import { CommonOutletTemplate } from "UI/CommonOutletTemplate";
import { API } from "aws-amplify";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { useRegions } from "hooks/useRegions";
import { ReactComponent as EditIcon } from "images/edit-big-icon.svg";
import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch } from "store/hooks";
import { hideSpinner, showSpinner } from "store/reducers/spinnerReducer";
import { ButtonPreview } from "./ButtonPreview";
import { EditPracticeDialog } from "./EditPracticeDialog";
import { ModalSpinner } from "./ModalSpinner";
import * as SC from "./RecipientDetails.style";

export const Container = styled(Stack)`
  flex: 1 1 auto;
  flex-direction: column;
  overflow-y: auto;
  gap: 1rem;
  padding: 1rem 2rem 0 2rem;

  @media screen and (max-width: 1024px) {
    padding: 1rem 1rem 0 1rem;
  }
`;

const EditBigIcon = styled(EditIcon)`
  cursor: pointer;
`;

export const MyOrganizationDetails = () => {
  const dispatch = useAppDispatch();
  const { createApiCall } = useCreateApiCall();
  const { userData } = useContext(GlobalContext);
  const { regionsOptions, refreshRegionsOptions } = useRegions();
  const id = userData?.organizationId;
  const [data, setData] = useState(null);

  const isAdmin = userData?.role === "Admin";
  const [isEditPracticeOpen, setIsEditPracticeOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const {
    name,
    speciality,
    phone,
    street,
    street2,
    city,
    state,
    zipCode,
    website,
  } =
    // @ts-ignore
    data?.Item ?? {};

  const getData = createApiCall(
    async () => {
      setIsLoading(true);
      // load recipient details by recipient id
      dispatch(showSpinner());
      const result = await API.get("restApi", `/recipients/${id}`, {});
      setData(result);
      dispatch(hideSpinner());
      setIsLoading(false);
    },
    () => setIsLoading(false)
  );

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CommonOutletTemplate noPadding title="My Organization Details">
      {!!data && (
        <Container>
          <Stack
            direction={"row"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            gap={2}
          >
            <SC.Title>{name}</SC.Title>
            {isAdmin && (
              <EditBigIcon onClick={() => setIsEditPracticeOpen(true)} />
            )}
          </Stack>
          <SC.Paper>
            <ButtonPreview
              id={id}
              name={name}
              imageFromS3={data?.imageFromS3}
            />
            <Stack
              width={"100%"}
              alignItems={"stretch"}
              gap={2}
              overflow={"hidden"}
            >
              <SC.InfoSection>
                <Stack p={2} gap={2}>
                  {speciality ? (
                    <SC.Info>
                      <SC.Label>Speciality:</SC.Label> {speciality}
                    </SC.Info>
                  ) : (
                    ""
                  )}
                  <SC.Info>
                    <SC.Label>Address:</SC.Label>{" "}
                    {street2 ? `${street}, ${street2}` : street}, {city},{" "}
                    {state}, {zipCode}
                  </SC.Info>
                  <SC.Info>
                    <SC.Label>Phone:</SC.Label> {phone}
                  </SC.Info>
                  {!!website && (
                    <SC.Info>
                      <SC.Label>Website:</SC.Label>{" "}
                      <a href={website} className="break-all">
                        {website}
                      </a>
                    </SC.Info>
                  )}
                </Stack>
              </SC.InfoSection>
            </Stack>
          </SC.Paper>
          {!regionsOptions && <ModalSpinner open={true} />}
        </Container>
      )}
      <EditPracticeDialog
        open={isEditPracticeOpen}
        practiceId={id}
        handleClose={() => setIsEditPracticeOpen(false)}
        refreshList={() => getData()}
        regionsOptions={regionsOptions}
        refreshRegionsOptions={refreshRegionsOptions}
        isRecipient
      />
      {isLoading && <ModalSpinner open={true} />}
    </CommonOutletTemplate>
  );
};
